import { getDatePicker } from './date-picker.js';
import { clamp, getMonthDate, monthsBetween } from './form-values.js';
import { RECALC_MONTH_SELECTOR } from './recalculation-list.js';

/** Fields (by name) that must stay inside the loan term. */
const TERM_DATE_FIELDS = [
  'discountStart',
  'discountEnd',
  'recurringStart',
  'recurringEnd',
  'adaptiveStart',
  'adaptiveEnd',
];

const LUMP_MONTH_SELECTOR = '#lump-list .lump-row input[data-field="month"]';

export type DateBounds = {
  /** Loan start month (YYYY-MM): the earliest month for any event. */
  min: string;
  /** Last month of the schedule horizon: the latest month for any overpayment or discount. */
  max: string;
  /** Last month of the original term: the latest month a refinance can happen. */
  refinanceMax: string;
};

export type DateBoundsInput = {
  startDate: string;
  termYears: number;
  refinanceEnabled: boolean;
  refinanceMonth: string;
  refinanceTerm: number;
};

/**
 * Month bounds for every date field, derived from the loan term. An enabled
 * refinance extends the horizon by its new term, counted from the refinance
 * month as it will be after clamping (a refinance can only happen while the
 * original loan runs), so the bounds stay consistent with the clamped form.
 */
export function dateBounds(state: DateBoundsInput): DateBounds {
  const termMonths = Math.max(1, Math.round(clamp(state.termYears, 1, 40) * 12));
  const refinanceMax = getMonthDate(state.startDate, termMonths - 1);
  let horizon = termMonths;
  if (state.refinanceEnabled) {
    const refinanceMonth = clamp(monthsBetween(state.startDate, state.refinanceMonth), 1, termMonths - 1);
    const refinanceTerm = Math.round(clamp(state.refinanceTerm, 1, 40) * 12);
    horizon = Math.max(horizon, refinanceMonth + refinanceTerm);
  }
  return {
    min: state.startDate,
    max: getMonthDate(state.startDate, horizon - 1),
    refinanceMax,
  };
}

function monthInput(form: HTMLFormElement, name: string): HTMLInputElement | null {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field : null;
}

/**
 * Applies `min`/`max` to a month field and clamps its value into the range
 * (a `min` above `max` collapses to `max`). Dispatches a non-bubbling `change`
 * when the value moved so the custom picker refreshes without re-entering the
 * form's recompute. Returns whether the value changed.
 */
function applyBounds(field: HTMLInputElement, min: string, max: string, fillEmpty: boolean): boolean {
  const lower = min > max ? max : min;
  field.min = lower;
  field.max = max;
  let next = field.value;
  if (fillEmpty && !next) next = lower;
  if (next && next < lower) next = lower;
  if (next && next > max) next = max;
  const changed = next !== field.value;
  if (changed) {
    field.value = next;
    field.dispatchEvent(new Event('change', { bubbles: false }));
  }
  const picker = getDatePicker(field);
  picker?.setMinDate(lower);
  picker?.setMaxDate(max);
  return changed;
}

/** Toggles `sheet-enabled` on every `.sheet-block[data-toggle]` from its checkbox. */
export function syncSheetStatuses(form: HTMLFormElement): void {
  form.querySelectorAll<HTMLElement>('.sheet-block[data-toggle]').forEach(block => {
    const toggleName = block.getAttribute('data-toggle');
    if (!toggleName) return;
    const control = form.elements.namedItem(toggleName);
    if (!(control instanceof HTMLInputElement)) return;
    block.classList.toggle('sheet-enabled', control.checked);
  });
}

/**
 * Every event date must lie inside the loan term: on or after the start date
 * and no later than the last scheduled month (a refinance: no later than the
 * last month of the original term). Returns the inputs whose value was rewritten
 * (empty when nothing changed) so the UI can tell the user what moved and where.
 */
export function syncAllDateConstraints(form: HTMLFormElement, state: DateBoundsInput): HTMLInputElement[] {
  const bounds = dateBounds(state);
  const clamped: HTMLInputElement[] = [];
  TERM_DATE_FIELDS.forEach(name => {
    const field = monthInput(form, name);
    if (field && applyBounds(field, bounds.min, bounds.max, false)) clamped.push(field);
  });
  form.querySelectorAll<HTMLInputElement>(LUMP_MONTH_SELECTOR).forEach(field => {
    if (applyBounds(field, bounds.min, bounds.max, false)) clamped.push(field);
  });
  // A recalculation needs at least one payment before it and lies inside the original term.
  const recalcMin = getMonthDate(state.startDate, 1);
  form.querySelectorAll<HTMLInputElement>(RECALC_MONTH_SELECTOR).forEach(field => {
    if (applyBounds(field, recalcMin, bounds.refinanceMax, false)) clamped.push(field);
  });
  const refinance = monthInput(form, 'refinanceMonth');
  if (refinance && applyBounds(refinance, bounds.min, bounds.refinanceMax, false)) clamped.push(refinance);
  syncRelatedDateConstraints(form, bounds.max).forEach(field => {
    if (!clamped.includes(field)) clamped.push(field);
  });
  return clamped;
}

/**
 * Period end dates may not precede their start dates (a one-month period has
 * end == start; both months count) and never pass `max`. Returns the rewritten inputs.
 */
export function syncRelatedDateConstraints(form: HTMLFormElement, max: string): HTMLInputElement[] {
  return [
    constrainEndNotBeforeStart(form, 'discountStart', 'discountEnd', max, true),
    constrainEndNotBeforeStart(form, 'recurringStart', 'recurringEnd', max, false),
    constrainEndNotBeforeStart(form, 'adaptiveStart', 'adaptiveEnd', max, false),
  ].filter((field): field is HTMLInputElement => field !== null);
}

function constrainEndNotBeforeStart(
  form: HTMLFormElement,
  startName: string,
  endName: string,
  max: string,
  fillEmpty: boolean,
): HTMLInputElement | null {
  const start = monthInput(form, startName);
  const end = monthInput(form, endName);
  if (!start || !end || !start.value) return null;
  return applyBounds(end, start.value, max, fillEmpty) ? end : null;
}

const CLAMP_NOTE_ATTR = 'data-clamp-note';

/**
 * Shows «Месяц изменён на …» under every field in `clamped` and removes the notes of
 * the previous run, so a note lives until the next edit. Lump rows and picker-wrapped
 * inputs both sit inside a `.field` label, which hosts the note.
 */
export function renderClampNotes(
  form: HTMLFormElement,
  clamped: HTMLInputElement[],
  monthLabel: (value: string) => string,
): void {
  form.querySelectorAll(`[${CLAMP_NOTE_ATTR}]`).forEach(note => note.remove());
  clamped.forEach(field => {
    const host = field.closest('.field');
    if (!host) return;
    const note = document.createElement('small');
    note.className = 'field-note';
    note.setAttribute(CLAMP_NOTE_ATTR, '');
    note.textContent = `Месяц изменён на ${monthLabel(field.value)}, чтобы попасть в срок расчёта`;
    host.append(note);
  });
}
